import { memo, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import CustomButton from "../Buttons/CustomButton";
import CustomTooltip from "../Buttons/CustomTooltip";
import ConversionLineChart from "../migrations/ConversionLineChart";
import RadarChartForConversion from "../migrations/RadarChartForConversion";
import SummaryDBMigration from "../DBMigration/SummaryDBMigration";
import SourceProfileDashboard from "../SourceProfile/SourceProfileDashboard";

const styles = {
  scrollContainer: {
    height: "85vh",
    maxHeight: "100vh",
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
};

// Memoized Card Components
const DatabasesCard = memo(({ loading, codeConversionData }) => {
  return (
    <div
      className="card border-0 card-migration mb-1"
      style={{ borderRadius: "8px", minHeight: "124px" }}
    >
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center">
              <div className="icons-bg me-2">
                  <i className="dash-icon-total-jobs"></i>
              </div>
              <h5
                className="card-title fw-bold mb-0 dbcardsdesign"
                style={{
                  height: "35px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Total Jobs
              </h5>
            </div>
            {loading ? (
              <div className="spinner-border spinner-border-sm text-secondary"></div>
            ) : (
              <h4 className="mb-2 mt-2 homecardfont">
                {codeConversionData?.cardMetrics?.total_jobs ??
                  "Status unavailable"}
              </h4>
            )}
            <h6
              className="text-muted mb-1"
              style={{ fontSize: "12px", whiteSpace: "nowrap" }}
            >
              No.of code conversion jobs
            </h6>
          </div>
        </div>
      </div>
    </div>
  );
});

const TablesmigratedCard = memo(({ loading, codeConversionData }) => {
  return (
    <div
      className="card border-0 card-migration mb-1"
      style={{ borderRadius: "8px", minHeight: "124px" }}
    >
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center">
              <div className="icons-bg me-2">
                  <i className="dash-icon-code-convert"></i>
              </div>
              
              <h5
                className="card-title fw-bold mb-0 dbcardsdesign"
                style={{
                  height: "35px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Code Converted
              </h5>
            </div>
            {loading ? (
              <div className="spinner-border spinner-border-sm text-secondary"></div>
            ) : (
              <h4 className="mb-2 mt-2 homecardfont">
                {codeConversionData?.cardMetrics?.code_converted ?? "N/A"}
              </h4>
            )}
            <h6
              className="text-muted mb-1"
              style={{ fontSize: "12px", whiteSpace: "nowrap" }}
            >
              Total code blocks converted
            </h6>
          </div>
        </div>
      </div>
    </div>
  );
});

const RunningmigrationsCard = memo(({ loading, codeConversionData }) => {
  return (
    <div
      className="card border-0 card-migration mb-1"
      style={{ borderRadius: "8px", minHeight: "124px" }}
    >
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center">
              <div className="icons-bg me-2">
                  <i className="fas fa-list-ul font-size-16"></i>
              </div>
              
              <h5
                className="card-title fw-bold mb-0 dbcardsdesign"
                style={{
                  height: "35px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                }}
              >
                Ongoing Conversion
              </h5>
            </div>
            {loading ? (
              <div className="spinner-border spinner-border-sm text-secondary"></div>
            ) : (
              <h4 className="mb-2 mt-2 homecardfont">
                {codeConversionData?.cardMetrics?.ongoing_conversions ?? "N/A"}
              </h4>
            )}
            <h6
              className="text-muted mb-1"
              style={{ fontSize: "12px", whiteSpace: "nowrap" }}
            >
              Tasks being converted now
            </h6>
          </div>
        </div>
      </div>
    </div>
  );
});

// Memoized StatusChart Component
const StatusChart = memo(({ title, loading, icon, jobsBySkill }) => {
  const [isRadarChartFullscreen, setIsRadarChartFullscreen] = useState(false);
  const radarChartContainerRef = useRef(null);

  const showEmptyState = useMemo(
    () => loading || !jobsBySkill || jobsBySkill.length === 0,
    [loading, jobsBySkill]
  );

  const toggleRadarChartFullscreen = () => {
    if (!document.fullscreenElement) {
      radarChartContainerRef.current
        .requestFullscreen()
        .then(() => setIsRadarChartFullscreen(true))
        .catch(console.error);
    } else {
      document
        .exitFullscreen()
        .then(() => setIsRadarChartFullscreen(false))
        .catch(console.error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      setIsRadarChartFullscreen(
        isFullscreen && document.fullscreenElement === radarChartContainerRef.current
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  return (
    <div 
      className="card card-migration min-h-100p mb-0 cursor-pointer"
      ref={radarChartContainerRef}
      style={{
        height: isRadarChartFullscreen ? "calc(100vh - 100px)" : "100%"
      }}
    >
      <div
        className="card-body p-3 d-flex flex-column"
        style={{ height: "100%" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="d-flex align-items-center dbcardheadsdesign">
            <div className="icons-bg me-2">
                  <span class="dash-icon-by-skills"><span class="path1"></span><span class="path2"></span><span class="path3"></span></span>
              </div>
            <span>{title}</span>
          </h6>
          
          {!showEmptyState && (
            <CustomTooltip
              content={
                isRadarChartFullscreen
                  ? "Exit fullscreen"
                  : "Enter fullscreen"
              }
              placement="left"
            >
              <i
                className={`mdi ${
                  isRadarChartFullscreen
                    ? "mdi-fullscreen-exit"
                    : "mdi-fullscreen"
                } text-dark font-size-22 fullscreen`}
                style={{ cursor: "pointer" }}
                onClick={toggleRadarChartFullscreen}
              />
            </CustomTooltip>
          )}
        </div>

        {!showEmptyState && (
          <p className="text-muted mb-2" style={{ fontSize: "12px" }}>
            Number of conversion jobs by source-to-target DB pairs
          </p>
        )}

        {showEmptyState ? (
          <div
            className="flex-grow-1 d-flex flex-column align-items-center justify-content-center"
            style={{ height: "200px" }}
          >
            <img
              src="assets/images/emptystate.gif"
              style={{ width: "180px", opacity: 0.7 }}
              alt="No Data Available"
            />
            <p className="text-muted mt-2">No skills data available</p>
          </div>
        ) : (
          <div 
            style={{ 
              height: isRadarChartFullscreen ? "calc(100vh - 150px)" : "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <RadarChartForConversion 
              data={jobsBySkill} 
              height={isRadarChartFullscreen ? "60vh" : "80%"}
            />
          </div>
        )}
      </div>
    </div>
  );
});

// Memoized MigrationProgress Component
const MigrationProgress = memo(
  ({
    title = "Conversions",
    icon = "assets/images/Migrations.svg",
    viewAllLink = "/",
    progressValue = 0,
    items = [],
    loading = false,
  }) => {
    const handleCheckError = useCallback((migrationId) => {
      console.log(`Checking error for migration ${migrationId}`);
    }, []);

    const hasData = items.length > 0;

    // Get the latest job with formatted status
    const latestJob = useMemo(() => {
      if (!items.length) return null;
      const job = items[0]; // Assuming items are already sorted by most recent
      
      // Format the status for display
        const formattedStatus = job?.status?.toLowerCase() === "completed"
        ? "Conversion Completed"
        : job?.status?.toLowerCase() === "failed"
        ? "Conversion Failed"
        : "Conversion Progress";
      
      return {
        ...job,
        formattedStatus,
        statusColor: job?.status?.toLowerCase() === "failed"
          ? "#dc3545"
          : job?.status?.toLowerCase() === "completed"
          ? "#198754"
          : "#0d6efd"
      };
    }, [items]);

    return (
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="d-flex align-items-center dbcardheadsdesign">
            <div className="icons-bg me-2">
                  <i className="dash-icon-code-conversion"></i>
            </div>
            <span>{title}</span>
          </h6>
          <h5
            className="card-title fw-bold mb-2"
            style={{ fontSize: "11px", textDecoration: "underline" }}
          >
            <a
              href={viewAllLink}
              style={{ color: "inherit", textDecoration: "inherit" }}
            >
              View All
            </a>
          </h5>
        </div>

        {!hasData ? (
          <div
            className="card border-0 card-migration mb-2 text-center py-4"
            style={{
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <img
              src="assets/images/emptystate.gif"
              alt="No Data"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "assets/images/emptystate.gif";
              }}
              style={{
                width: "200px",
                objectFit: "contain",
                marginBottom: "12px",
              }}
            />
            <p className="text-muted" style={{ fontSize: "14px" }}>
              No recent conversions found
            </p>
          </div>
        ) : (
          <>
            <div
              className="card border-0 card-migration mb-2"
              style={{ borderRadius: "8px" }}
            >
              <div className="card-body p-3 mb-[-2]">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="card-title fw-bold mb-2 dbcardsdesign">
                    {latestJob ? (
                      <>
                         {latestJob.job_name || "Unnamed"} :{" "}
                        <span style={{ color: latestJob.statusColor }}>
                          {latestJob.formattedStatus}
                        </span>
                      </>
                    ) : (
                      "Conversion Progress"
                    )}
                  </h5>
                  <span
                    className="badge"
                    style={{
                      fontSize: "12px",
                      backgroundColor: "white",
                      color: "black",
                    }}
                  >
                    {progressValue}%
                  </span>
                </div>

                <div className="progress mb-2" style={{ height: "8px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      // backgroundColor: "#8298DD",
                      width: `${progressValue || 0}%`,
                    }}
                    aria-valuenow={parseInt(progressValue || "0")}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>

                <h6 className="text-muted" style={{ fontSize: "12px" }}>
                  Track the progress of your ongoing conversions.
                </h6>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-secondary"></div>
              </div>
            ) : (
              items.map((item, index) => (
                <div
                  key={item.id || index}
                  className="card border-0 card-migration mb-2"
                  style={{
                    borderRadius: "8px",
                    borderLeft: `4px solid ${
                      item?.status?.toLowerCase() === "failed"
                        ? "#dc3545"
                        : item?.status?.toLowerCase() === "completed"
                        ? "#0d6efd"
                        : "#198754"
                    }`,
                  }}
                >
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center">
                      <div>
                        <div className="icons-bg2 me-2">
                              <i className="dash-icon-database"></i>
                          </div>
                      </div>

                      <div className="w-100">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h5 className="card-title fw-bold mb-0 dbcardsdesign">
                            {item?.job_name
                              ? item.job_name.replace(
                                  /\w\S*/g,
                                  (txt) =>
                                    txt.charAt(0).toUpperCase() +
                                    txt.substr(1).toLowerCase()
                                )
                              : "Unnamed"}

                            <img
                              src={
                                item?.status?.toLowerCase() === "completed"
                                  ? "/assets/images/migratesuc.gif"
                                  : item?.status?.toLowerCase() === "failed"
                                  && "/assets/images/migratefail.gif"
                                  
                              }
                              // alt={item?.status?.toLowerCase()}
                              style={{
                                borderRadius: "80%",
                                objectFit: "cover",
                                border: "2px solid #fff",
                              }}
                              width={
                                item?.status === "failed"
                                  ? "23px"
                                  : item?.status === "completed"
                                  && "35px"
                                  
                              }
                              height={
                                item?.status === "failed"
                                  ? "24px"
                                  : item?.status === "completed"
                                  ? "35px"
                                  : "25px"
                              }
                              className={
                                item?.status === "failed" ||
                                item?.status === "completed"
                                  ? "ms-2"
                                  : ""
                              }
                            />
                          </h5>

                          {item?.status === "failed" ? (
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              style={{
                                fontSize: "12px",
                                color: "#202B3E",
                              }}
                              onClick={() => handleCheckError(item?.id)}
                            >
                              Check
                            </button>
                          ) : (
                            <span
                              className="badge ms-2"
                              style={{
                                fontSize: "12px",
                                backgroundColor: "white",
                                color: "black",
                                border: "1px solid #dee2e6",
                              }}
                            >
                              {item?.progress ||
                                (item?.status === "completed" ? "100" : "0")}
                              %
                            </span>
                          )}
                        </div>

                        <h6
                          className="text-muted mb-0"
                          style={{ fontSize: "12px" }}
                        >
                          {item?.status?.toLowerCase() === "completed"
                            ? "This conversion was completed."
                            : item?.status?.toLowerCase() === "failed"
                            ? "This conversion has failed."
                            : "This conversion is currently running."}
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    );
  }
);

// Memoized RecentActivities Component
const RecentActivities = memo(
  ({ title, activities, loading, icon, noActivitiesMessage, itemLabel }) => {
    const truncateText = useCallback((text, maxLength) => {
      if (!text) return "N/A";

      const isTruncated = text.length > maxLength;
      const truncated = isTruncated
        ? `${text.substring(0, maxLength)}...`
        : text;

      return isTruncated ? (
        <CustomTooltip
          content={text}
          placement="right"
          fallbackPlacements={["top"]}
        >
          <span className="truncated-text" style={{ cursor: "pointer" }}>
            {truncated}
          </span>
        </CustomTooltip>
      ) : (
        <span>{text}</span>
      );
    }, []);

    const validActivities = useMemo(() => {
      return activities?.filter((activity) => activity?.job_name) || [];
    }, [activities]);

    const formattedActivities = useMemo(() => {
      return validActivities.map((activity) => ({
        ...activity,
        completed_at: activity.completed_at || "N/A",
        num_tables_migrated: activity.num_files_converted || 0,
      }));
    }, [validActivities]);

    const showEmptyState = useMemo(
      () => loading || !validActivities || validActivities.length === 0,
      [loading, validActivities]
    );

    return (
      <div className="card card-migration h-100 w-100 mb-0 cursor-pointer">
        <div
          className="card-body p-3 d-flex flex-column"
          style={{ height: "100%" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="d-flex align-items-center">
              <div className="icons-bg me-2">
                  <i className="dash-icon-recent-code"></i>
              </div>
              <h6 className="dbcardheadsdesign mb-0">{title}</h6>
            </div>

            {formattedActivities.length > 0 && (
              <h5
                className="card-title fw-bold mb-0"
                style={{ fontSize: "11px", textDecoration: "underline" }}
              >
                <a
                  href="/migration"
                  style={{ color: "inherit", textDecoration: "inherit" }}
                >
                  View All
                </a>
              </h5>
            )}
          </div>

          <div
            className={`flex-grow-1 d-flex flex-column ${
              showEmptyState
                ? "align-items-center justify-content-center"
                : ""
            }`}
            style={{
              minHeight: "270px",
              overflowY: "auto",
              transition: "all 0.3s ease",
            }}
          >
            {showEmptyState ? (
              <div 
                className="d-flex flex-column align-items-center justify-content-center h-100 w-100"
                style={{
                  transition: "opacity 0.3s ease",
                  opacity: loading ? 0.8 : 1
                }}
              >
                <img
                  src="assets/images/emptystate.gif"
                  style={{ 
                    width: "180px", 
                    opacity: 0.7, 
                    marginBottom: "0px",
                    transition: "opacity 0.3s ease"
                  }}
                  alt="No activities found"
                />
                <p 
                  className="text-muted mb-0"
                  style={{ transition: "opacity 0.3s ease" }}
                >
                  {loading ? "Loading activities..." : noActivitiesMessage}
                </p>
              </div>
            ) : (
              <div style={{ width: "100%" }}>
                {formattedActivities.map((activity, idx, arr) => (
                  <div
                    key={`${activity.id || idx}`}
                    className="d-flex"
                    style={{ marginBottom: "20px" }}
                  >
                    <div
                      style={{
                        width: "40px",
                        position: "relative",
                        top: "5px",
                      }}
                    >
                      <div className="icons-bg3 me-2">
                          <i className="dash-icon-database"></i>
                      </div>
                      {idx !== arr.length - 1 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "25px",
                            bottom: "-20px",
                            left: "10.5px",
                            width: "2px",
                            backgroundColor: "#ECEAEE",
                          }}
                        />
                      )}
                    </div>

                    <div
                      style={{
                        flex: 1,
                        paddingLeft: "0px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        marginTop: "5px",
                      }}
                    >
                      <div
                        className="d-flex align-items-center"
                        style={{
                          justifyContent: "space-between",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <strong className="stat-text2"
                          style={{
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: "13px",
                           
                            maxWidth: "110px",
                            minWidth: "110px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {truncateText(activity.job_name, 10)}
                        </strong>

                        <div
                          className="d-flex align-items-center text-muted"
                          style={{
                            fontSize: "12px",
                            minWidth: "135px",
                            justifyContent: "flex-end",
                         marginLeft: "auto", 
                            alignItems: "center",
                          }}
                        >
                          <i
                            className="ri-calendar-2-line"
                            style={{ fontSize: "14px", marginRight: "4px" }}
                          ></i>
                          {activity.completed_at}
                        </div>
                      </div>

                      <small
                        className="text-muted"
                        style={{ fontSize: "12px" }}
                      >
                        {activity.num_tables_migrated} {itemLabel}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
// Custom hook for WebSocket management with throttling
const useThrottledWebSocket = (
  url,
  eventType,
  updateCallback,
  dependencies = []
) => {
  const socketRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const maxRetryDelay = 16000;
  const lastUpdateRef = useRef(0);
  const throttleDelay = 1000;

  const throttledUpdate = useCallback(
    (data) => {
      const now = Date.now();
      if (now - lastUpdateRef.current >= throttleDelay) {
        lastUpdateRef.current = now;
        updateCallback(data);
      }
    },
    [updateCallback, throttleDelay]
  );

  useEffect(() => {
    const connectWebSocket = () => {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        console.error("Authentication token missing");
        return;
      }

      socketRef.current = new WebSocket(`${url}?token=${authToken}`);

      socketRef.current.onopen = () => {
        console.log(`${eventType} WebSocket connected`);
        retryDelayRef.current = 1000;
      };

      socketRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.event === eventType) {
            throttledUpdate(message.data);
          }
        } catch (err) {
          console.error("WebSocket message parsing error:", err);
        }
      };

      socketRef.current.onclose = (event) => {
        console.warn(`${eventType} WebSocket closed:`, event);
        if (!document.hidden) {
          setTimeout(connectWebSocket, retryDelayRef.current);
          retryDelayRef.current = Math.min(
            retryDelayRef.current * 2,
            maxRetryDelay
          );
        }
      };

      socketRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        socketRef.current?.close();
      };
    };

    const handleVisibilityChange = () => {
      if (document.hidden && socketRef.current) {
        socketRef.current.close();
      } else if (
        !document.hidden &&
        (!socketRef.current ||
          socketRef.current.readyState === WebSocket.CLOSED)
      ) {
        connectWebSocket();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    connectWebSocket();

    return () => {
      if (socketRef.current) socketRef.current.close();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, dependencies);
};

function Home({ setShow, setCurrentStep, activeTab, setActiveTab, profile }) {
  const location = useLocation();
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();
  const { state } = useAuth();
  const [loading, setLoading] = useState(true);
  const [homeActiveTab, setHomeActiveTab] = useState("source_profiler");

  const [codeConversionData, setCodeConversionData] = useState({
    recentlyCompletedActivities: [],
    weeklyLineChart: [],
    cardMetrics: {
      total_jobs: 0,
      code_converted: 0,
      ongoing_conversions: 0,
      failed_conversions: 0,
      success_rate: 0,
    },
    conversionProgress: {
      recentConversions: [],
      overall_progress: 0,
    },
    jobsBySkill: [],
  });

  const socketRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const maxRetryDelay = 16000;

  const fetchCodeConversionData = useCallback(() => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("Token missing for WebSocket");
      return;
    }

    if (socketRef.current) {
      socketRef.current.close();
    }

    setLoading(true);

    const connectWebSocket = () => {
      const wsBaseUrl =
        process.env.REACT_APP_API_URL_CODE_CONVERSION_WEB_SOCKET;
      socketRef.current = new WebSocket(
        `${wsBaseUrl}/ws/get_conversion_reports?token=${token}`
      );

      socketRef.current.onopen = () => {
        console.log("Connected to WebSocket for code conversion reports");
        retryDelayRef.current = 1000;
        setLoading(false);
      };

      socketRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.event === "user_reports_update") {
            const data = message.data;

            setCodeConversionData({
              recentlyCompletedActivities:
                data.recently_completed_activities || [],
              weeklyLineChart: data.weekly_line_chart || [],
              cardMetrics: {
                total_jobs: data.card_metrics?.total_jobs || 0,
                code_converted: data.card_metrics?.code_converted || 0,
                ongoing_conversions:
                  data.card_metrics?.ongoing_conversions || 0,
                failed_conversions: data.card_metrics?.failed_conversions || 0,
                success_rate: data.card_metrics?.success_rate || 0,
              },
              conversionProgress: {
                recentConversions:
                  data.conversion_progress?.recent_conversions || [],
                overall_progress:
                  data.conversion_progress?.overall_progress || 0,
              },
              jobsBySkill: data.jobs_by_skill || [],
            });
          }
        } catch (err) {
          console.error("WebSocket message parse error:", err);
        }
      };

      socketRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        socketRef.current?.close();
      };

      socketRef.current.onclose = (event) => {
        console.warn("WebSocket closed unexpectedly:", event);
        if (!document.hidden) {
          setTimeout(connectWebSocket, retryDelayRef.current);
          retryDelayRef.current = Math.min(
            retryDelayRef.current * 2,
            maxRetryDelay
          );
        }
      };
    };

    connectWebSocket();
  }, []);

  useEffect(() => {
    fetchCodeConversionData();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [fetchCodeConversionData]);

  const handleCreateNewJob = useCallback(
    (e) => {
      e.preventDefault();
      navigate("/migration", { state: { openCreateModal: true } });
    },
    [navigate]
  );
const handleCreateNewDataMigrationJob = useCallback(
  (e) => {
    e.preventDefault();
    navigate("/listmigration", { 
      state: { 
        openCreateDatabase: true,
        databaseStep: 1 
      } 
    });
  },
  [navigate]
);
  
  const handleCreateNewScanJob = useCallback(
    (e) => {
      e.preventDefault();
      navigate("/sourceprofiler", { state: { openCreateModal: true } });
    },
    [navigate]
  );
  const handleTabChange = useCallback((tab) => {
    setHomeActiveTab(tab);
  }, []);

  const greeting = useMemo(
    () => profile?.first_name || state.user?.first_name || "Guest",
    [profile?.first_name, state.user?.first_name]
  );

  const chartData = useMemo(
    () => codeConversionData.weeklyLineChart,
    [codeConversionData.weeklyLineChart]
  );

  return (
    <div
      id="home"
      className={`${
        activeTab === "#home" ? "" : "hidden"
      } tab_content font-family-inter`}
    >
      <div className="page-content">
        <div className="container-fluid" >
          <div className="d-flex justify-content-between">
            <div>
              <h5 className="fw-bold mb-0">Home</h5>
              <div className="page-title-right">
                <ol className="breadcrumb m-0 p-0">
                  <li className="breadcrumb-item font-size-12">
                    <a href="javascript: void(0);">Home</a>
                  </li>
                  <li className="breadcrumb-item font-size-12 active ps-0 pe-0">
                    <span className="text-dark"> Dashboard</span>
                  </li>
                </ol>
              </div>
            </div>
             <div className="d-flex" style={{ gap: "8px" }}>
              {homeActiveTab === "code_migration" ? (
                <CustomButton
                  onClick={handleCreateNewJob}
                  text="Start Code Conversion"
                  className="btn btn-primary custom-button"
                  text2="New Job"
                />
              ) : homeActiveTab === "source_profiler" ? (
                <CustomButton
                  onClick={handleCreateNewScanJob}
                  text="Create a New Scan Job"
                  className="btn btn-primary custom-button"
                  text2="New Scan"
                />
                ) : homeActiveTab === "data_migration" ? (
    <CustomButton
      onClick={handleCreateNewDataMigrationJob}
      text="Start Database Migration"
      className="btn btn-primary custom-button"
      text2="New Job"
    />
  ) : null}
            </div>
          </div>

        <div
  className="qabefore mh-calc-120"
  style={{ 
    overflowY: "scroll", 
    overflowX: "hidden",
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)",
    paddingLeft:"32px",
    paddingRight:"4px"

  }}
>
  <div className="container-fluid p-0">
    <div
      className="page-cont1main qabefore ps-0 pe-md-0"
      style={{
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
        position: "relative"
      }}
    >
                <h2 
  className="fw-sbold pt-4 mb-0"
  style={{
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)"
  }}
>
  Hi {greeting}
</h2>
<h2 
  className="text-secondary fw-medium mb-3"
  style={{
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)"
  }}
>
  {homeActiveTab === "source_profiler" 
    ? "Focused on database structure profiling" 
    : homeActiveTab === "data_migration" 
    ? "Your central hub for managing database migrations"
    : "Suggests improving/upgrading legacy code"}
</h2>
<h6 
  className="fw-medium mb-3 text-con" 
  style={{ 
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)"
  }}
>
  Ready to take your database migrations to the next level?
</h6>
              </div>

              {/* <div>
                <ul
                  className="nav nav-tabs nav-tabs-custom nav-justified w-fit-content mb-1"
                  role="tablist"
                > */}
                <div
  style={{
    transform: "translateZ(0)",
    WebkitTransform: "translateZ(0)",
    position: "relative",
    zIndex: 10
  }}
>
  <ul
    className="nav nav-tabs nav-tabs-custom nav-justified w-fit-content mb-1"
    role="tablist"
    style={{
      transform: "translateZ(0)",
      WebkitTransform: "translateZ(0)"
    }}
  >

         <li
                    className="nav-item"
                    role="presentation"
                    style={{ borderBottom: "2px solid #BBC0C2" }}
                  >
                    <button
                      className={`nav-link tab-col w-fit-content ${
                        homeActiveTab === "source_profiler" ? "active" : ""
                      }`}
                      onClick={() => handleTabChange("source_profiler")}
                      style={{
                        backgroundColor:
                          homeActiveTab === "source_profiler"
                            ? "#dcdfe3"
                            : "transparent",
                        borderRadius: "8px 8px 0 0",
                        padding: "10px 20px",
                        width: "140px",
                        height: "29px",
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "21px",
                        letterSpacing: "0%",
                        position: "relative",
                        zIndex: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <span className="d-sm-block white-space-nowrap">
                        Source Profiler
                        {homeActiveTab === "source_profiler" && (
                          <span
                            style={{
                              display: "block",
                              height: "1px",
                              // backgroundColor: "#354258",
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              zIndex: 4,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </li>
                  <li
                    className="nav-item"
                    role="presentation"
                    style={{ borderBottom: "2px solid #BBC0C2" }}
                  >
                    <button
                      className={`nav-link tab-col w-fit-content ${
                        homeActiveTab === "data_migration" ? "active" : ""
                      }`}
                      onClick={() => handleTabChange("data_migration")}
                      style={{
                        backgroundColor:
                          homeActiveTab === "data_migration"
                            ? "#dcdfe3"
                            : "transparent",
                        borderRadius: "8px 8px 0 0",
                        padding: "10px 20px",
                        width: "140px",
                        height: "29px",
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "21px",
                        letterSpacing: "0%",
                        position: "relative",
                        zIndex: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <span className="d-sm-block white-space-nowrap">
                        Data Migration
                        {homeActiveTab === "data_migration" && (
                          <span
                            style={{
                              display: "block",
                              height: "1px",
                              // backgroundColor: "#354258",
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              zIndex: 4,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </li>
                  <li
                    className="nav-item"
                    role="presentation"
                    style={{ borderBottom: "2px solid #BBC0C2" }}
                  >
                    <button
                      className={`nav-link tab-col w-fit-content ${
                        homeActiveTab === "code_migration" ? "active" : ""
                      }`}
                      onClick={() => handleTabChange("code_migration")}
                      style={{
                        backgroundColor:
                          homeActiveTab === "code_migration"
                            ? "#dcdfe3"
                            : "transparent",
                        borderRadius: "8px 8px 0 0",
                        padding: "10px 20px",
                        width: "140px",
                        height: "29px",
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "21px",
                        letterSpacing: "0%",
                        position: "relative",
                        zIndex: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <span className="d-sm-block white-space-nowrap">
                        Code Conversion
                        {homeActiveTab === "code_migration" && (
                          <span
                            style={{
                              display: "block",
                              height: "1px",
                              // backgroundColor: "#354258",
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              zIndex: 4,
                            }}
                          />
                        )}
                      </span>
                    </button>
                  </li>
                 
                </ul>
              </div>

              {homeActiveTab === "data_migration" ? (
                <SummaryDBMigration
                  setShow={setShow}
                  setCurrentStep={setCurrentStep}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  profile={profile}
                />
              ) : homeActiveTab === "code_migration" ? (
                <>
                  <div className="container-fluid"
                
      style={{ 
         padding:"0px 0px 1px 0px",marginTop:"-4px",marginLeft:"-2px",paddingRight:"2px"
       }}
                  >
                    <div
                      className="row g-2 mb-2 align-items-stretch"
                      style={{ minHeight: "400px", marginTop: "4px",paddingLeft:"5px"}}
                    >
                      <div className="col-lg-8 col-md-7 d-flex flex-column">
                        <div className="row g-2 mb-2">
                          <div className="col-md-4">
                            <DatabasesCard
                              loading={loading}
                              codeConversionData={codeConversionData}
                            />
                          </div>
                          <div className="col-md-4">
                            <TablesmigratedCard
                              loading={loading}
                              codeConversionData={codeConversionData}
                            />
                          </div>
                          <div className="col-md-4">
                            <RunningmigrationsCard
                              loading={loading}
                              codeConversionData={codeConversionData}
                            />
                          </div>
                        </div>

                        <div
                          className="flex-grow-1"
                          style={{ marginTop: "3px" }}
                        >
                          <MigrationProgress
                            title="Code Conversions"
                            icon="assets/images/Migrations.svg"
                            viewAllLink="/migration"
                            progressValue={
                              codeConversionData.conversionProgress
                                .overall_progress
                            }
                            items={
                              codeConversionData.conversionProgress?.recentConversions?.map(
                                (item) => ({
                                  ...item,
                                  progress: item.completion_percentage,
                                  id: item.job_name,
                                  job_name: item.job_name,
                                  status: item.status,
                                })
                              ) || []
                            }
                            loading={loading}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-lg-4 col-md-5">
                        <div style={{ height: "98.56%", marginBottom: "0px" }}>
                          <StatusChart
                            title="Jobs by Skills"
                            loading={loading}
                            icon="assets/images/Migrationsbystatus.png"
                            jobsBySkill={codeConversionData.jobsBySkill}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row g-2 mb-2 align-items-stretch" 
                    // style={{marginTop:"-15px"}}
                      style={{  paddingLeft:"5px",marginTop:"-16px" }}
                    >
                      <div className="col-lg-8 col-md-7">
                        <div className="border-0 h-100 w-100">
                          <ConversionLineChart data={chartData} />
                        </div>
                      </div>
                      <div className="col-12 col-lg-4 col-md-5">
                        <RecentActivities
                          title="Recent Code Activities"
                          activities={
                            codeConversionData.recentlyCompletedActivities
                          }
                          icon="assets/images/recentactiivities.svg"
                          noActivitiesMessage="No recent code activities yet"
                          itemLabel="files converted"
                          loading={loading}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : homeActiveTab === "source_profiler" ? (
                <SourceProfileDashboard
                  setShow={setShow}
                  setCurrentStep={setCurrentStep}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  profile={profile}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
